<template>
    <div>
        <div class="tw-w-full tw-grid tw-grid-cols-12 tw-gap-6">
            <!-- Main Profile Container -->
            <div class="tw-col-span-12 lg:tw-col-start-3 lg:tw-col-end-11 xl:tw-col-start-4 xl:tw-col-end-10">
                
                <!-- Unified Profile Card -->
                <div class="tw-p-6 tw-bg-white lg:tw-rounded-lg lg:tw-border tw-border-y tw-border-solid tw-border-primary-200">
                    
                    <!-- Profile Header -->
                    <div class="tw-flex tw-items-center tw-justify-center tw-flex-col tw-text-center tw-mb-8">
                        <div class="tw-w-[100px] tw-h-[100px] tw-rounded-full tw-overflow-hidden tw-border-3 tw-border-solid tw-border-primary-300 tw-mb-3">
                            <img :src="'https://ui-avatars.com/api/?name=' + profileData.name + '&background=10b981&color=fff'" alt="avatar" class="tw-w-full tw-h-full tw-rounded-full">
                        </div>
                        <h1 class="tw-text-2xl tw-font-bold tw-text-primary-950 tw-mb-1">{{ profileData.name }}</h1>
                        <p class="tw-text-base tw-text-gray-600 tw-capitalize">{{ profileData.title }} @ <span class="tw-text-primary-500 tw-font-medium">{{ profileData.company }}</span></p>
                    </div>

                    <!-- About Section -->
                    <div class="tw-mb-6">
                        <div class="tw-flex tw-justify-between tw-items-center tw-mb-3 tw-pb-2 tw-border-b tw-border-solid tw-border-primary-100">
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <div class="tw-w-7 tw-h-7 tw-bg-gray-100 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                                    <icon icon="solar:document-text-bold-duotone" class="tw-text-gray-600 tw-text-base" />
                                </div>
                                <h2 class="tw-text-lg tw-font-bold tw-text-primary-950">About</h2>
                            </div>
                            <button @click="editSection('about')" class="tw-text-primary-500 tw-font-medium tw-text-sm hover:tw-text-primary-600 tw-duration-200">
                                Edit
                            </button>
                        </div>
                        <p class="tw-text-gray-700 tw-leading-relaxed tw-text-sm">{{ profileData.about }}</p>
                    </div>

                    <!-- Country Section -->
                    <div class="tw-mb-6">
                        <div class="tw-flex tw-justify-between tw-items-center tw-mb-3 tw-pb-2 tw-border-b tw-border-solid tw-border-primary-100">
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <div class="tw-w-7 tw-h-7 tw-bg-gray-100 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                                    <icon icon="solar:map-point-bold-duotone" class="tw-text-gray-600 tw-text-base" />
                                </div>
                                <h2 class="tw-text-lg tw-font-bold tw-text-primary-950">Country</h2>
                            </div>
                            <button @click="editSection('country')" class="tw-text-primary-500 tw-font-medium tw-text-sm hover:tw-text-primary-600 tw-duration-200">
                                Edit
                            </button>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-3">
                            <span class="tw-text-xl">{{ profileData.country.flag }}</span>
                            <span class="tw-text-gray-800 tw-font-medium tw-text-sm">{{ profileData.country.name }}</span>
                        </div>
                    </div>

                    <!-- Social Media Section -->
                    <div class="tw-mb-6">
                        <div class="tw-flex tw-justify-between tw-items-center tw-mb-3 tw-pb-2 tw-border-b tw-border-solid tw-border-primary-100">
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <div class="tw-w-7 tw-h-7 tw-bg-gray-100 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                                    <icon icon="solar:share-bold-duotone" class="tw-text-gray-600 tw-text-base" />
                                </div>
                                <h2 class="tw-text-lg tw-font-bold tw-text-primary-950">Social Media</h2>
                            </div>
                            <button @click="editSection('social')" class="tw-text-primary-500 tw-font-medium tw-text-sm hover:tw-text-primary-600 tw-duration-200">
                                Edit
                            </button>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                            <a 
                                v-if="profileData.socialMedia.linkedin" 
                                :href="profileData.socialMedia.linkedin" 
                                target="_blank"
                                class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-bg-gray-100 tw-rounded-lg tw-text-blue-600 hover:tw-bg-blue-50 hover:tw-text-blue-700 tw-duration-200 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-blue-300"
                                title="LinkedIn"
                            >
                                <icon icon="mingcute:linkedin-fill" class="tw-text-lg" />
                            </a>
                            <a 
                                v-if="profileData.socialMedia.instagram" 
                                :href="profileData.socialMedia.instagram" 
                                target="_blank"
                                class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-bg-gray-100 tw-rounded-lg tw-text-pink-600 hover:tw-bg-pink-50 hover:tw-text-pink-700 tw-duration-200 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-pink-300"
                                title="Instagram"
                            >
                                <icon icon="mingcute:instagram-fill" class="tw-text-lg" />
                            </a>
                            <a 
                                v-if="profileData.socialMedia.twitter" 
                                :href="profileData.socialMedia.twitter" 
                                target="_blank"
                                class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-bg-gray-100 tw-rounded-lg tw-text-sky-600 hover:tw-bg-sky-50 hover:tw-text-sky-700 tw-duration-200 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-sky-300"
                                title="Twitter"
                            >
                                <icon icon="mingcute:twitter-fill" class="tw-text-lg" />
                            </a>
                            <a 
                                v-if="profileData.socialMedia.facebook" 
                                :href="profileData.socialMedia.facebook" 
                                target="_blank"
                                class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-bg-gray-100 tw-rounded-lg tw-text-blue-700 hover:tw-bg-blue-50 hover:tw-text-blue-800 tw-duration-200 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-blue-300"
                                title="Facebook"
                            >
                                <icon icon="mingcute:facebook-fill" class="tw-text-lg" />
                            </a>
                            <a 
                                v-if="profileData.socialMedia.youtube" 
                                :href="profileData.socialMedia.youtube" 
                                target="_blank"
                                class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-bg-gray-100 tw-rounded-lg tw-text-red-600 hover:tw-bg-red-50 hover:tw-text-red-700 tw-duration-200 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-red-300"
                                title="YouTube"
                            >
                                <icon icon="mingcute:youtube-fill" class="tw-text-lg" />
                            </a>
                            <a 
                                v-if="profileData.socialMedia.github" 
                                :href="profileData.socialMedia.github" 
                                target="_blank"
                                class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-bg-gray-100 tw-rounded-lg tw-text-gray-800 hover:tw-bg-gray-200 hover:tw-text-gray-900 tw-duration-200 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-gray-400"
                                title="GitHub"
                            >
                                <icon icon="mingcute:github-fill" class="tw-text-lg" />
                            </a>
                        </div>
                    </div>

                    <!-- Contact Details Section -->
                    <div class="tw-mb-6">
                        <div class="tw-flex tw-justify-between tw-items-center tw-mb-3 tw-pb-2 tw-border-b tw-border-solid tw-border-primary-100">
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <div class="tw-w-7 tw-h-7 tw-bg-gray-100 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                                    <icon icon="solar:phone-bold-duotone" class="tw-text-gray-600 tw-text-base" />
                                </div>
                                <h2 class="tw-text-lg tw-font-bold tw-text-primary-950">Contact details</h2>
                            </div>
                            <button @click="editSection('contact')" class="tw-text-primary-500 tw-font-medium tw-text-sm hover:tw-text-primary-600 tw-duration-200">
                                Edit
                            </button>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-3">
                            <icon icon="solar:global-bold-duotone" class="tw-text-lg tw-text-gray-500" />
                            <a :href="'https://' + profileData.contact.website" target="_blank" class="tw-text-gray-800 tw-font-medium hover:tw-text-primary-500 tw-duration-200 tw-text-sm">
                                {{ profileData.contact.website }}
                            </a>
                        </div>
                    </div>

                    <!-- Events Section -->
                    <div>
                        <div class="tw-flex tw-justify-between tw-items-center tw-mb-3 tw-pb-2 tw-border-b tw-border-solid tw-border-primary-100">
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <div class="tw-w-7 tw-h-7 tw-bg-gray-100 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                                    <icon icon="solar:calendar-bold-duotone" class="tw-text-gray-600 tw-text-base" />
                                </div>
                                <h2 class="tw-text-lg tw-font-bold tw-text-primary-950">Is attending</h2>
                            </div>
                            <button @click="editSection('events')" class="tw-text-primary-500 tw-font-medium tw-text-sm hover:tw-text-primary-600 tw-duration-200">
                                Edit
                            </button>
                        </div>
                        <div class="tw-space-y-3">
                            <div 
                                v-for="event in profileData.events" 
                                :key="event.id"
                                class="tw-flex tw-items-center tw-justify-between tw-p-3 tw-border tw-border-solid tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50 tw-duration-200"
                            >
                                <div class="tw-flex-1">
                                    <h3 class="tw-font-semibold tw-text-gray-900 tw-text-sm">{{ event.title }}</h3>
                                    <p class="tw-text-xs tw-text-gray-600">{{ event.date }}</p>
                                </div>
                                <icon icon="solar:arrow-right-outline" class="tw-text-lg tw-text-gray-400" />
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </div>

        <!-- Edit Modals -->
        <a-popup :visible="editModalVisible" @cancel="closeEditModal">
            <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[600px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-lg tw-my-5 tw-rounded-lg">
                <div class="tw-p-6 tw-border-b tw-border-solid tw-border-gray-200 tw-flex tw-justify-between tw-items-center">
                    <h3 class="tw-text-lg tw-font-semibold tw-text-gray-800">Edit {{ currentEditSection }}</h3>
                    <button @click="closeEditModal" class="tw-text-gray-400 hover:tw-text-gray-600 tw-duration-200">
                        <icon icon="solar:close-line-linear" class="tw-text-xl" />
                    </button>
                </div>

                <div class="tw-p-6">
                    <!-- About Edit Form -->
                    <div v-if="currentEditSection === 'about'">
                        <FormField
                            v-model="editForm.about"
                            type="textarea"
                            label="About"
                            placeholder="Tell us about yourself..."
                            :error="editErrors.about"
                        />
                    </div>

                    <!-- Country Edit Form -->
                    <div v-if="currentEditSection === 'country'">
                        <FormField
                            v-model="editForm.country"
                            type="select"
                            label="Country"
                            :options="countryOptions"
                            :error="editErrors.country"
                        />
                    </div>

                    <!-- Social Media Edit Form -->
                    <div v-if="currentEditSection === 'social'" class="tw-space-y-4">
                        <FormField
                            v-model="editForm.linkedin"
                            type="url"
                            label="LinkedIn Profile"
                            placeholder="https://linkedin.com/in/username"
                            left-icon="mingcute:linkedin-fill"
                            :error="editErrors.linkedin"
                        />
                        <FormField
                            v-model="editForm.instagram"
                            type="url"
                            label="Instagram Profile"
                            placeholder="https://instagram.com/username"
                            left-icon="mingcute:instagram-fill"
                            :error="editErrors.instagram"
                        />
                        <FormField
                            v-model="editForm.twitter"
                            type="url"
                            label="Twitter Profile"
                            placeholder="https://twitter.com/username"
                            left-icon="mingcute:twitter-fill"
                            :error="editErrors.twitter"
                        />
                        <FormField
                            v-model="editForm.facebook"
                            type="url"
                            label="Facebook Profile"
                            placeholder="https://facebook.com/username"
                            left-icon="mingcute:facebook-fill"
                            :error="editErrors.facebook"
                        />
                        <FormField
                            v-model="editForm.youtube"
                            type="url"
                            label="YouTube Channel"
                            placeholder="https://youtube.com/@username"
                            left-icon="mingcute:youtube-fill"
                            :error="editErrors.youtube"
                        />
                        <FormField
                            v-model="editForm.github"
                            type="url"
                            label="GitHub Profile"
                            placeholder="https://github.com/username"
                            left-icon="mingcute:github-fill"
                            :error="editErrors.github"
                        />
                    </div>

                    <!-- Contact Edit Form -->
                    <div v-if="currentEditSection === 'contact'">
                        <FormField
                            v-model="editForm.website"
                            type="url"
                            label="Website"
                            placeholder="www.example.com"
                            left-icon="solar:global-bold-duotone"
                            :error="editErrors.website"
                        />
                    </div>

                    <!-- Events Edit Form -->
                    <div v-if="currentEditSection === 'events'">
                        <p class="tw-text-gray-600 tw-text-sm">Event management functionality will be available soon.</p>
                    </div>
                </div>

                <div class="tw-p-6 tw-border-t tw-border-solid tw-border-gray-200 tw-flex tw-justify-end tw-gap-3">
                    <a-button @click="closeEditModal" intent="simple" class="tw-min-w-[100px]">
                        Cancel
                    </a-button>
                    <a-button @click="saveChanges" intent="primary" :loading="saving" class="tw-min-w-[100px]">
                        Save Changes
                    </a-button>
                </div>
            </div>
        </a-popup>
    </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useAuthStore } from '@/stores'
import FormField from '@/components/form/FormField.vue'

const authStore = useAuthStore()

// Demo profile data
const profileData = reactive({
    name: authStore.user?.name || 'Sarah Johnson',
    title: 'CEO',
    company: 'TechInnovate Solutions',
    about: 'Lead generation continues to be a huge source of frustration for many businesses, with 63% of them pointing it out as their biggest marketing challenge. Sales reps spend a lot of time and money pursuing leads, only to lose 1 (or more) out of every 4 in the process.',
    country: {
        code: 'CN',
        name: 'China',
        flag: '🇨🇳'
    },
    socialMedia: {
        linkedin: 'https://linkedin.com/in/sarahjohnson',
        instagram: 'https://instagram.com/sarahjohnson',
        twitter: 'https://twitter.com/sarahjohnson',
        facebook: 'https://facebook.com/sarah.johnson',
        youtube: 'https://youtube.com/@sarahjohnson',
        github: 'https://github.com/sarahjohnson'
    },
    contact: {
        website: 'www.khalildc.com'
    },
    events: [
        {
            id: 1,
            title: 'Successfully Grow a Sustainable Team',
            date: 'Sun, Nov 21, 2025 | 3:00 PM',
            type: 'workshop',
            status: 'attending'
        },
        {
            id: 2,
            title: 'Successfully Grow a Sustainable Team',
            date: 'Sun, Nov 21, 2025 | 3:00 PM',
            type: 'workshop',
            status: 'attending'
        },
        {
            id: 3,
            title: 'Successfully Grow a Sustainable Team',
            date: 'Sun, Nov 21, 2025 | 3:00 PM',
            type: 'workshop',
            status: 'attending'
        }
    ]
})

// Edit modal state
const editModalVisible = ref(false)
const currentEditSection = ref('')
const saving = ref(false)

// Edit form data
const editForm = reactive({
    about: '',
    country: '',
    linkedin: '',
    instagram: '',
    twitter: '',
    facebook: '',
    youtube: '',
    github: '',
    website: ''
})

// Edit form errors
const editErrors = reactive({
    about: '',
    country: '',
    linkedin: '',
    instagram: '',
    twitter: '',
    facebook: '',
    youtube: '',
    github: '',
    website: ''
})

// Country options for dropdown
const countryOptions = [
    { id: 'CN', name: 'China' },
    { id: 'US', name: 'United States' },
    { id: 'GB', name: 'United Kingdom' },
    { id: 'FR', name: 'France' },
    { id: 'DE', name: 'Germany' },
    { id: 'JP', name: 'Japan' },
    { id: 'KR', name: 'South Korea' },
    { id: 'IN', name: 'India' },
    { id: 'BR', name: 'Brazil' },
    { id: 'CA', name: 'Canada' }
]

// Country flags mapping
const countryFlags = {
    'CN': '🇨🇳',
    'US': '🇺🇸',
    'GB': '🇬🇧',
    'FR': '🇫🇷',
    'DE': '🇩🇪',
    'JP': '🇯🇵',
    'KR': '🇰🇷',
    'IN': '🇮🇳',
    'BR': '🇧🇷',
    'CA': '🇨🇦'
}

// Edit section
const editSection = (section) => {
    currentEditSection.value = section
    
    // Pre-populate form with current data
    switch (section) {
        case 'about':
            editForm.about = profileData.about
            break
        case 'country':
            editForm.country = profileData.country.code
            break
        case 'social':
            editForm.linkedin = profileData.socialMedia.linkedin
            editForm.instagram = profileData.socialMedia.instagram
            editForm.twitter = profileData.socialMedia.twitter
            editForm.facebook = profileData.socialMedia.facebook
            editForm.youtube = profileData.socialMedia.youtube
            editForm.github = profileData.socialMedia.github
            break
        case 'contact':
            editForm.website = profileData.contact.website
            break
    }
    
    // Clear errors
    Object.keys(editErrors).forEach(key => {
        editErrors[key] = ''
    })
    
    editModalVisible.value = true
}

// Close edit modal
const closeEditModal = () => {
    editModalVisible.value = false
    currentEditSection.value = ''
    
    // Clear form
    Object.keys(editForm).forEach(key => {
        editForm[key] = ''
    })
}

// Save changes
const saveChanges = async () => {
    saving.value = true
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Update profile data based on section
    switch (currentEditSection.value) {
        case 'about':
            if (editForm.about.trim()) {
                profileData.about = editForm.about
            }
            break
        case 'country':
            if (editForm.country) {
                const selectedCountry = countryOptions.find(c => c.id === editForm.country)
                if (selectedCountry) {
                    profileData.country.code = editForm.country
                    profileData.country.name = selectedCountry.name
                    profileData.country.flag = countryFlags[editForm.country] || '🏳️'
                }
            }
            break
        case 'social':
            profileData.socialMedia.linkedin = editForm.linkedin
            profileData.socialMedia.instagram = editForm.instagram
            profileData.socialMedia.twitter = editForm.twitter
            profileData.socialMedia.facebook = editForm.facebook
            profileData.socialMedia.youtube = editForm.youtube
            profileData.socialMedia.github = editForm.github
            break
        case 'contact':
            if (editForm.website) {
                profileData.contact.website = editForm.website.replace(/^https?:\/\//, '')
            }
            break
    }
    
    saving.value = false
    closeEditModal()
}
</script>